import React, { createContext, useContext, useCallback, useRef, useMemo, useState } from 'react';
import { Block, BlockSchemaFromSpecs } from '@blocknote/core';
import { CustomBlockContentSpec } from 'pages/in-app/worksheet/Worksheet';
import { useDebounce } from 'hooks/useDebounce';

type WorksheetSessionContextValue = {
  onBlockSubmit: (document: any[]) => void;
  isSaving: boolean;
};

const WorksheetSessionContext = createContext<WorksheetSessionContextValue | null>(null);

export const useWorksheetSession = (): WorksheetSessionContextValue | null => {
  const context = useContext(WorksheetSessionContext);
  return context; // Return null if not in provider (e.g., teacher preview mode)
};

type WorksheetSessionProviderProps = {
  children: React.ReactNode;
  onSave: (document: Block<BlockSchemaFromSpecs<CustomBlockContentSpec>>[]) => Promise<void>;
};

export const WorksheetSessionProvider: React.FC<WorksheetSessionProviderProps> = ({
  children,
  onSave,
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const pendingDocumentRef = useRef<Block<BlockSchemaFromSpecs<CustomBlockContentSpec>>[] | null>(
    null
  );

  // Debounced save function
  const debouncedSave = useDebounce(
    async () => {
      if (isSaving || !pendingDocumentRef.current) return;

      const documentToSave = pendingDocumentRef.current;
      pendingDocumentRef.current = null; // Clear after capturing

      setIsSaving(true);
      try {
        await onSave(documentToSave);
      } catch (error) {
        console.error('Auto-save failed:', error);
      } finally {
        setIsSaving(false);
      }
    },
    500 // 500ms debounce
  );

  const onBlockSubmit = useCallback(
    (document: Block<BlockSchemaFromSpecs<CustomBlockContentSpec>>[]) => {
      // Store the latest document (will overwrite previous if called multiple times)
      pendingDocumentRef.current = document;
      // Trigger debounced save
      debouncedSave();
    },
    [debouncedSave]
  );

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      onBlockSubmit,
      isSaving,
    }),
    [onBlockSubmit, isSaving]
  );

  return (
    <WorksheetSessionContext.Provider value={contextValue}>
      {children}
    </WorksheetSessionContext.Provider>
  );
};
